import React from 'react';
import classnames from 'classnames';

import { makeStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import { getStatus } from '@hydra/utils/criterium';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';
import { getHtmlFromDraftState } from '@hydra/routes/pdf/utils/moduleHelpers';

const useStyles = makeStyles(theme => ({
  root: {
    breakInside: 'avoid',
    backgroundColor: theme.palette.secondary.smoke,
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  inner: {
    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
      marginRight: 0
    }
  },
  success: {
    borderLeft: `5px solid ${theme.palette.success.medium}`
  },
  warning: {
    borderLeft: `5px solid ${theme.palette.warning.medium}`
  },
  error: {
    borderLeft: `5px solid ${theme.palette.error.medium}`
  },
  grey: {
    borderLeft: `5px solid ${theme.palette.secondary.main}`
  },
  modern: {
    fontSize: theme.typography.fontSizeBase,
    padding: theme.spacing(1.5, 2.5),
    margin: theme.spacing(2.5, 0),
    marginLeft: 0,
    borderLeftWidth: 2
  },
  classic: {
    fontSize: theme.typography.fontSizeMedium,
    padding: theme.spacing(1.5, 2.5),
    margin: theme.spacing(2.5, 0),
    marginLeft: theme.spacing(3)
  },
  default: {
    width: '100%',
    padding: theme.spacing(2)
  }
}));

interface CriteriumAdviceProps {
  score: number | null
  type: string,
  variables?: Record<string, string>
}

export const CriteriumAdvice = ({ score, type, variables }: CriteriumAdviceProps) => {
  const classes = useStyles();
  const { status, color } = getStatus(score);
  const statusKey = `${type}_${status}_content`;

  const { isPdf, isAssistant } = useReviewContext();
  const { customCriteria = {}, externalLinks = false, applyCustomCriteria = false, pdfTheme } = usePdfTemplateContext();
  if (isPdf) {
    const customAdvice = applyCustomCriteria && customCriteria[type]
      ? getHtmlFromDraftState(customCriteria[type].customText)
      : null;

    const themeClass = pdfTheme === 'modern' ? classes.modern : classes.classic;
    return (
      <div className={classnames(classes.root, themeClass, classes[color])}>
        {customAdvice
          ? <div dangerouslySetInnerHTML={{ __html: customAdvice }} />
          : (
            <>
              <Translate content={statusKey} with={variables} allowLinks={externalLinks} />
              <Translate content={`${type}_content`} with={variables} allowLinks={externalLinks} />
            </>
            )}
      </div>
    );
  }

  return (
    <div
      className={classnames(
        classes.root,
        classes.inner,
        classes.default,
        classes[color]
      )}
    >
      <Translate content={statusKey} with={variables} allowLinks={!isAssistant} />
      <Translate content={`${type}_content`} with={variables} allowLinks={!isAssistant} />
    </div>
  );
};

export default CriteriumAdvice;
